/* eslint-disable @typescript-eslint/no-explicit-any */
import { Group, FabricImage, Textbox } from 'fabric';

import { useCanvasStore } from '../store';

import { MenuItemWithBackup } from '../types/types';
import { TextEntity } from '../metaBullets/classes/TextEntity';
import { Marker } from '../metaBullets/classes/Marker';

import { deleteMaskImage } from './crop';
import { isUserImage } from './imageUtils';
import { isBullet, isBulletTextItem, performActionOnObjectInGroup } from './groupUtils';

/**
 * Находит и удаляет orphaned contentObjects маркеров
 */
function findAndDeleteOrphanedMarkerContent(canvas: any, entityIds: string[]): string[] {
  const deletedContentIds: string[] = [];

  // Находим все объекты которые могут быть контентом маркеров
  const allObjects = canvas.getObjects();
  const markerContentObjects = allObjects.filter(
    (obj: any) => obj.isMarkerContent === true || obj.markerId,
  );

  console.debug('🔍 Looking for orphaned marker content:', {
    entityIds,
    foundContentObjects: markerContentObjects.length,
    contentIds: markerContentObjects.map((obj: any) => ({
      id: obj.id,
      markerId: obj.markerId,
      entityId: obj.entityId,
    })),
  });

  // Удаляем контент связанный с удаляемыми сущностями
  markerContentObjects.forEach((contentObj: any) => {
    if (contentObj.entityId && entityIds.includes(contentObj.entityId)) {
      console.debug('🗑️ Deleting orphaned marker content:', {
        contentId: contentObj.id,
        markerId: contentObj.markerId,
        entityId: contentObj.entityId,
      });

      canvas.remove(contentObj);
      deletedContentIds.push(contentObj.id);
    }
  });

  if (deletedContentIds.length > 0) {
    console.debug('✅ Deleted orphaned marker content:', deletedContentIds);
  }

  return deletedContentIds;
}

/**
 * Отключает связь конкретного маркера с TextEntity
 */
function disconnectMarkerFromTextEntity(canvas: any, markerId: string): void {
  const allObjects = canvas.getObjects();

  // Находим TextEntity связанную с этим маркером
  const textEntity = allObjects.find(
    (obj: any) => TextEntity.isTextEntity(obj) && obj.markerId === markerId,
  ) as TextEntity | undefined;

  if (textEntity) {
    console.debug('🔌 Disconnecting marker from text entity:', {
      entityId: textEntity.id,
      markerId,
    });

    // ✅ Очищаем связь с маркером через публичный метод
    textEntity.clearMarkerReference();

    console.debug('✅ Marker disconnected from text entity:', {
      entityId: textEntity.id,
    });
  }
}

/**
 * Очищает оставшийся контент маркеров после их удаления
 */
function cleanupRemainingMarkerContent(canvas: any, markerIds: string[]): string[] {
  const deletedContentIds: string[] = [];
  const allObjects = canvas.getObjects();

  // Ищем orphaned contentObjects от удаленных маркеров
  const markerContentObjects = allObjects.filter(
    (obj: any) =>
      (obj.isMarkerContent === true || obj.markerId) &&
      markerIds.some((markerId) => obj.markerId === markerId),
  );

  console.debug('🔍 Looking for orphaned marker content:', {
    markerIds,
    foundContentObjects: markerContentObjects.length,
  });

  markerContentObjects.forEach((contentObj: any) => {
    console.debug('🗑️ Removing orphaned content from direct marker deletion:', {
      contentId: contentObj.id,
      markerId: contentObj.markerId,
    });

    canvas.remove(contentObj);
    deletedContentIds.push(contentObj.id);
  });

  return deletedContentIds;
}

export const deleteItems = ({
  targetIds,
  canvas,
  canvasId,
  addBackup,
}: Omit<MenuItemWithBackup, 'item'>) => {
  const idsToDelete = Array.isArray(targetIds) ? targetIds : [targetIds];
  const activeObjects = canvas.getActiveObjects();
  let isContentChanged = false; // Flag for tracking content changes (text, image, bullet item)

  // Собираем ID текстовых сущностей для поиска связанных маркеров
  const textEntityIds: string[] = [];
  // ✅ Собираем ID маркеров для специальной обработки
  const markerIds: string[] = [];

  // ✅ Получаем MetaBulletSystem и BackupManager
  const metaBulletSystem = (canvas as any)._metaBulletSystem;
  const backupManager = metaBulletSystem?.getBackupManager();

  // Сначала проходим по всем объектам и собираем ID разных типов
  idsToDelete.forEach((currentId) => {
    const targetObject = canvas.getObjects().find((object: any) => object.id === currentId);
    if (targetObject) {
      if (TextEntity.isTextEntity(targetObject)) {
        textEntityIds.push(currentId);
      } else if (targetObject.type === 'marker' && targetObject.isMarker === true) {
        markerIds.push(currentId);
      }
    }
  });

  // ✅ Определяем тип операции и начинаем управление бекапами
  let operationType: 'marker_delete' | 'entity_delete' | 'mixed_delete' = 'mixed_delete';
  if (markerIds.length > 0 && textEntityIds.length === 0) {
    operationType = 'marker_delete';
  } else if (textEntityIds.length > 0 && markerIds.length === 0) {
    operationType = 'entity_delete';
  }

  // ✅ Начинаем операцию удаления в BackupManager
  if (backupManager) {
    backupManager.startOperation(operationType);
    console.debug('🔄 Started MetaBullet backup operation:', {
      operationType,
      textEntityIds,
      markerIds,
    });
  }

  // ✅ Обрабатываем прямое удаление маркеров ПЕРЕД основным циклом
  if (markerIds.length > 0) {
    console.debug('🎯 Pre-processing direct marker deletion:', { markerIds });

    // Используем новый метод safeDelete для маркеров
    const allObjects = canvas.getObjects();
    const markersToDelete = allObjects.filter(
      (obj: any) => obj.type === 'marker' && obj.isMarker === true && markerIds.includes(obj.id),
    ) as Marker[];

    markersToDelete.forEach((marker: Marker) => {
      // Отключаем связь с TextEntity
      disconnectMarkerFromTextEntity(canvas, marker.id);
      // Вызываем безопасное удаление
      marker.safeDelete();
    });
  }

  idsToDelete.forEach((currentId) => {
    const targetObject = canvas.getObjects().find((object) => {
      if (object.id === currentId) {
        return true;
      } else if (object.category) {
        // If the object to delete is in a group (bullets)
        if (isBullet(object) && object instanceof Group) {
          performActionOnObjectInGroup(object, currentId, (obj) => {
            object.remove(obj); // Remove object from group
          });
          return; // Object found and processed
        }
      }
    });

    // Check if the object is a text, user-image or bullet item
    if (targetObject) {
      // ✅ Специальная обработка для TextEntity
      if (TextEntity.isTextEntity(targetObject)) {
        console.debug('🗑️ Using safeDelete for TextEntity:', targetObject.id);

        // Удаляем из layers store
        const activeCanvasId = useCanvasStore.getState().activeCanvasId;
        if (activeCanvasId) {
          try {
            const canvasState = useCanvasStore.getState().getCanvasState(activeCanvasId);
            const layerObjects =
              canvasState?.layersMap?.[targetObject.layer as keyof typeof canvasState.layersMap];

            if (
              layerObjects &&
              layerObjects.some((item: any) => item.object.id === targetObject.id)
            ) {
              useCanvasStore.getState().removeLayerObject(activeCanvasId, targetObject.layer, {
                object: targetObject,
                zIndex: targetObject.zIndex,
                layer: targetObject.layer,
              });
            }
          } catch (error) {
            console.warn('⚠️ Error removing TextEntity from layers store:', error);
          }
        }

        // Вызываем безопасное удаление
        targetObject.safeDelete();

        // Удаляем с канваса
        canvas.remove(targetObject);

        isContentChanged = true;
        return;
      }

      if (
        targetObject instanceof Textbox ||
        targetObject.type?.toLowerCase() === 'textbox' ||
        targetObject instanceof FabricImage ||
        isUserImage(targetObject) ||
        isBullet(targetObject) ||
        isBulletTextItem(targetObject)
      ) {
        isContentChanged = true; // Set flag isChangedContent
      }

      if (
        targetObject &&
        targetObject.category !== undefined &&
        targetObject.category !== null &&
        targetObject.type.toLowerCase() === 'image' &&
        targetObject.category.toLowerCase() === 'crop'
      ) {
        const imageObject = targetObject as FabricImage;
        deleteMaskImage(canvas, imageObject);
      }

      const activeCanvasId = useCanvasStore.getState().activeCanvasId;
      if (activeCanvasId) {
        // ✅ Безопасное удаление из layers с проверкой существования
        try {
          const canvasState = useCanvasStore.getState().getCanvasState(activeCanvasId);
          const layerObjects =
            canvasState?.layersMap?.[targetObject.layer as keyof typeof canvasState.layersMap];

          if (
            layerObjects &&
            layerObjects.some((item: any) => item.object.id === targetObject.id)
          ) {
            console.debug('🗑️ Removing object from layers store:', {
              objectId: targetObject.id,
              objectType: targetObject.type,
              layer: targetObject.layer,
            });

            useCanvasStore.getState().removeLayerObject(activeCanvasId, targetObject.layer, {
              object: targetObject,
              zIndex: targetObject.zIndex,
              layer: targetObject.layer,
            });
          } else {
            console.debug('⚠️ Object not found in layers, skipping store removal:', {
              objectId: targetObject.id,
              objectType: targetObject.type,
              layer: targetObject.layer,
            });
          }
        } catch (error) {
          console.warn('⚠️ Error removing object from layers store:', error);
        }
      }
      canvas.remove(targetObject);
    }
  });

  // ✅ Используем новый метод safeDelete для TextEntity (уже удалены в основном цикле)
  if (textEntityIds.length > 0) {
    // TextEntity уже удалены в основном цикле с помощью safeDelete
    // Дополнительная очистка orphaned контента на всякий случай
    const deletedContentIds = findAndDeleteOrphanedMarkerContent(canvas, textEntityIds);
    console.debug('🎯 Cleaned up orphaned content for deleted text entities:', {
      textEntityIds,
      deletedContentIds,
    });
  }

  // ✅ Дополнительная очистка контента маркеров (если что-то осталось)
  if (markerIds.length > 0) {
    const deletedDirectMarkerContentIds = cleanupRemainingMarkerContent(canvas, markerIds);
    console.debug('🎯 Cleaned up remaining marker content:', {
      markerIds,
      deletedContentIds: deletedDirectMarkerContentIds,
    });

    // НОВАЯ КОНЦЕПЦИЯ: После удаления маркера нужно пересчитать позиции текста
    // Находим все TextEntity которые были связаны с удаленными маркерами
    const allObjects = canvas.getObjects();
    const affectedMetaBulletIds = new Set<string>();

    allObjects.forEach((obj: any) => {
      if (TextEntity.isTextEntity(obj)) {
        const entity = obj as TextEntity;
        // Проверяем была ли эта сущность связана с удаленным маркером
        if (entity.metaBulletId) {
          affectedMetaBulletIds.add(entity.metaBulletId);
        }
      }
    });

    // Пересчитываем позиции для всех затронутых MetaBullet'ов
    if (affectedMetaBulletIds.size > 0) {
      console.debug('🔄 Recalculating positions after marker deletion:', {
        affectedMetaBulletIds: Array.from(affectedMetaBulletIds),
      });

      // Получаем MetaBulletSystem из canvas
      const metaBulletSystem = (canvas as any)._metaBulletSystem;
      if (metaBulletSystem) {
        affectedMetaBulletIds.forEach((metaBulletId) => {
          const metaBullet = metaBulletSystem.getMetaBullet(metaBulletId);
          if (metaBullet) {
            metaBullet.recalculatePositions();
            console.debug('✅ Recalculated positions for MetaBullet:', metaBulletId);
          }
        });
      } else {
        console.warn('⚠️ MetaBulletSystem not found on canvas');
      }
    }
  }

  if (activeObjects.length > 0) {
    canvas.discardActiveObject();
  }

  // ✅ Завершаем операцию удаления и создаем бекап
  if (backupManager) {
    // Запрашиваем бекап если были изменения
    if (isContentChanged || textEntityIds.length > 0 || markerIds.length > 0) {
      backupManager.requestBackup();
    }
    backupManager.finishOperation();
    console.debug('✅ Finished MetaBullet backup operation:', { operationType, isContentChanged });
  } else {
    // Fallback к старой логике если BackupManager не доступен
    const cfg = canvas.toJSON();

    // Set flag isChangedContent, if content was deleted (text, image, bullet item)
    if (isContentChanged) {
      cfg.isChangedContent = true; // Need for backend for layouts
    }

    addBackup(canvasId, cfg);
    console.debug('⚠️ Using fallback backup logic (BackupManager not available)');
  }

  canvas.requestRenderAll();
  // loadObjectsWithLayers(canvasId, cfg);
};
